<template>
  <v-menu v-if="canPerformAnyAction(advance)" bottom offset-y>
    <template #activator="{ on, attrs }">
      <v-btn data-testid="advance-menu" icon v-bind="attrs" v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
    </template>
    <v-list dense class="py-0">
      <v-list-item v-if="canSign" data-testid="advance-sign" @click="openSignDialog(advance)">
        <v-list-item-icon>
          <v-icon>mdi-file-sign</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{ $t('advanceActionSign') }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-list-item v-if="canSubmit(advance)" data-testid="advance-submit" @click="openSubmitDialog(advance)">
        <v-list-item-icon>
          <v-icon>mdi-email-fast-outline</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{ $t('advanceActionSubmit') }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-list-item v-if="canPublish(advance)" data-testid="advance-publish" @click="publish(advance)">
        <v-list-item-icon>
          <v-icon>mdi-hand-extended</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{ $t('advanceActionPublish') }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
      <v-list-item v-if="canEdit(advance)" data-testid="advance-edit" @click="openEditDialog(advance)">
        <v-list-item-icon>
          <v-icon>mdi-pencil</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{ $t('advanceActionEdit') }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
      <v-list-item v-if="canDestroy(advance)" data-testid="advance-destroy" @click="destroy(advance)">
        <v-list-item-icon>
          <v-icon color="error">mdi-delete</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{ $t('advanceActionDelete') }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
import { api } from '@/api'
import { createLink } from '@/api/helpers'

export default {
  name: 'AdvanceMenu',
  props: {
    advance: {
      type: Object,
      required: true,
    },
    canSign: {
      type: Boolean,
      default: () => true,
    },
    meeting: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
  },
  computed: {
    isResponsible() {
      return this.user.hasRole('responsible')
    },
    canPerformAnyAction() {
      return (advance) =>
        this.canSign ||
        this.canSubmit(advance) ||
        this.canPublish(advance) ||
        this.canEdit(advance) ||
        this.canDestroy(advance)
    },
  },
  methods: {
    isFirstSignatory(advance) {
      return this.user.id === advance.first_signatory.id
    },
    isInDraft(advance) {
      return advance.status === 'DRAFT'
    },
    isOpen(advance) {
      return advance.status === 'OPEN'
    },
    isSubmitted(advance) {
      return advance.status === 'SUBMITTED'
    },
    canEdit(advance) {
      if (this.isSubmitted(advance)) {
        return false
      }
      if (this.isResponsible) {
        return true
      }
      if (this.isOpen(advance)) {
        return false
      }
      return this.isFirstSignatory(advance)
    },
    canSubmit(advance) {
      return this.isOpen(advance) && this.isFirstSignatory(advance) && this.meeting.is_running_meeting
    },
    canPublish(advance) {
      return this.isInDraft(advance) && this.isFirstSignatory(advance)
    },
    canDestroy(advance) {
      if (this.isResponsible) {
        return true
      }
      if (this.isSubmitted(advance)) {
        return false
      }
      return this.isFirstSignatory(advance)
    },
    openEditDialog(advance) {
      this.$emit('edit', advance)
    },
    openSignDialog(advance) {
      this.$emit('sign', advance)
    },
    openSubmitDialog(advance) {
      this.$emit('submit', advance)
    },
    async publish(advance) {
      try {
        await api.post(createLink('advance/{id}/open', { id: advance.id }))
        this.$emit('published')
        this.notifySuccess(this.$t('advanceCardPublishSuccess'))
      } catch (error) {
        this.notifyError(this.$t('advanceCardPublishError'))
        throw error
      }
    },
    async destroy(advance) {
      if (!confirm(this.$t('advancesDestroyConfirmation'))) {
        return
      }
      try {
        await api.delete(createLink('advance/{id}', { id: advance.id }))
        this.$emit('destroyed')
        this.notifySuccess(this.$t('advanceCardDestroySuccess'))
      } catch (error) {
        this.notifyError(this.$t('advanceCardDestroyError'))
        throw error
      }
    },
  },
}
</script>
